import * as PropTypes from "prop-types";
import Modal from "./_modal";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import Tooltip from "./tipTool";
import { cn, computeImageDimension, dataUrlTobase64, rotate } from "./utils";
import {
  LuCamera,
  LuChevronLeft,
  LuChevronRight,
  LuChevronUpCircle,
  LuCrop,
  LuFlashlight,
  LuLoader2,
  LuPaperclip,
  LuPlusCircle,
  LuRefreshCcw,
  LuRotateCw,
  LuTrash2,
  LuUndo2,
  LuX,
  LuZoomIn,
  LuZoomOut,
} from "react-icons/lu";
import { Button } from "../CommonComponents";
import { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState, useTransition } from "react";
import Browser from "./_imageBrowser";
import ImageEditor from "./ImageEditor";
import store from "../PDFEditor/store";
import Upload from "./Upload";
import Pagination from "./Pagination";
import Scanner from "./_scanner";
import { Drawer, DrawerContent, DrawerTrigger } from "./drawer";
import { Command, CommandGroup, CommandItem, CommandList } from "./command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./dialog";

const ImageEditorModal = forwardRef(function ImageEditorModal(props, ref) {
  const image = useRef();
  const upload = useRef();
  const modal = useRef();

  const { filesData, editedFilesData, onChange, setFilesData, acceptTypes, compact, onClose } = props;

  const [open, setOpen] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [openDelete, setOpenDelete] = useState(false);
  const [isPending, startTransition] = useTransition();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [scale, setScale] = useState(0.50);

  function suspense(cb) {
    startTransition(() => cb());
  }

  useEffect(() => {
    (async () => {
      if (!editedFilesData) return setSelectedFile(null);
      const fileData = editedFilesData.find((file) => file.id === selectedFile?.id) || editedFilesData[0];
      setSelectedFile({ ...fileData, data: `data:${fileData.type};base64,${fileData.data}` });
    })();
  }, [editedFilesData]);

  useLayoutEffect(() => {
    if (!selectedFile) return;
    (async () => {
      if (!modal.current) return;
      const offset = compact ? { x: 62, y: 317 } : undefined;
      const { width, height } = await computeImageDimension(modal.current, selectedFile, offset);
      setSize({ width: width * scale, height: height * scale });
    })();
  }, [selectedFile, scale, isFullscreen, open]);

  async function handleRotate() {
    const data = await rotate(selectedFile, size);
    const existingData = store.get("image-edit") || {};
    delete existingData?.[selectedFile.id];
    store.update("image-edit", existingData);

    const filesData_ = [...editedFilesData].map((file) =>
      selectedFile.id === file.id ? { ...file, data: dataUrlTobase64(data) } : file
    );
    onChange(filesData_);
  }

  function handleReset() {
    const filesData_ = [...editedFilesData].map((file, idx) =>
      selectedFile.id === file.id ? { ...file, data: filesData[idx].data } : file
    );
    onChange(filesData_);
  }

  function handleDelete() {
    const filesData_ = [...filesData].filter((file) => file.id !== selectedFile.id);
    let selectedFileIndex = filesData.findIndex((file) => file.id === selectedFile?.id);
    selectedFileIndex = selectedFileIndex === 0 ? 1 : selectedFileIndex - 1;
    const selectedFile_ = editedFilesData?.[selectedFileIndex];
    setSelectedFile(
      selectedFile_ ? { ...selectedFile_, data: `data:${selectedFile_.type};base64,${selectedFile_.data}` } : null
    );

    setFilesData(filesData_.length ? filesData_ : null);
    setOpenDelete(false);
  }

  function handleAddFiles(files = []) {
    setFilesData((filesData || []).concat(files));
  }

  function handleChange(image) {
    const filesData_ = [...editedFilesData].map((file) =>
      image.id === file.id ? { ...file, data: dataUrlTobase64(image.data) } : file
    );
    onChange(filesData_);
  }
  
  function handleZoomIn() {
  
    if (scale < 1) { // Limiting zoom in to 200% of original size
  
      setScale(scale + 0.25);
  
    }
  
  }
   
  function handleZoomOut() {
  
    if (scale > 0.25) { // Limiting zoom out to 50% of original size
  
      setScale(scale - 0.25);
  
    }
  
  }
  
  const controls = [
    <div key="ctrls" className="flex items-center justify-between w-fit gap-1 py-1 ">
      <Popover open={openDelete} onOpenChange={(open) => setOpenDelete(isCapturing ? false : open)}>
        <PopoverTrigger>
          <Icon key="delete" label="Delete" className="text-red-500" description="Permanently delete" disabled={isCapturing}>
            <LuTrash2 />
          </Icon>
        </PopoverTrigger>
        <PopoverContent container={ref.current} className="w-[330px] bg-white flex items-center justify-between">
          <div>Do you want to delete?</div>
          <Button variant="destructive" size="sm" onClick={handleDelete} disabled={isCapturing}>
            delete
          </Button>
        </PopoverContent>
      </Popover>
      {/* <Icon
        key="add-image"
        label="Add File"
        description="Add more files"
        onClick={() => upload.current.mount()}
        disabled={isCapturing}
      >
        <LuPaperclip />
      </Icon> */}
      {/* <Icon
        key="capture-iamge"
        active={isCapturing}
        label="Capture"
        description="Take files from your camera"
        onClick={() => setIsCapturing(!isCapturing)}
      >
        <LuCamera />
      </Icon> */}
      <Icon key="undo" label="Reset" description="Revert back to original" onClick={handleReset} disabled={isCapturing}>
        <LuUndo2 />
      </Icon>
      <Icon
        key="rotate"
        label="Rotate"
        description="Rotate image clockwise"
        onClick={() => suspense(handleRotate)}
        disabled={isCapturing}
      >
        <LuRotateCw />
      </Icon>
      <Icon
        key="crop"
        label="Crop"
        description="Crop your image"
        onClick={() => suspense(image.current.crop)}
        disabled={isCapturing}
      >
        <LuCrop />
      </Icon>
      <Icon
        key="zoomin"
        label="Zoom In"
        description="Zooms in your image"
        onClick={handleZoomIn}
        disabled={isCapturing || scale >= 1}
      >
        <LuZoomIn />
      </Icon>
      <Icon
        key="zoomout"
        label="Zoom out"
        description="Zooms out your image"
        onClick={handleZoomOut}
        disabled={isCapturing || scale <= 0.25}
      >
        <LuZoomOut />
      </Icon>
    </div>,
  ];

  const cameraCtrls = [<LuFlashlight key="flash" />];

  useImperativeHandle(
    ref,
    () => {
      if (compact) {
        return {
          showModal: () => setOpen(true),
          closeModal: () => setOpen(false),
        };
      }
      return modal.current;
    },
    [compact, open]
  );

  const [item, setItem] = useState("trash");

  if (!compact) {
    return (
      <>
        <Modal
          ref={modal}
          title=""
          controls={{ header: { comp: controls } }}
          onFullScreenChange={setIsFullscreen}
          className="overflow-hidden"
        >
          <div className="flex h-full items-center">
            <Browser
              imageSrcs={editedFilesData?.map?.((file) => ({ ...file, data: `data:${file.type};base64,${file.data}` }))}
              selected={selectedFile}
              onClick={setSelectedFile}
            />
            <div className="relative flex flex-col justify-center h-full w-full">
              {isCapturing ? (
                <Scanner container={modal.current} isFullscreen={isFullscreen} onCapture={handleAddFiles} />
              ) : selectedFile ? (
                <>
                  <div className="flex flex-col justify-center h-full w-full">
                    <ImageEditor
                      ref={image}
                      width={size.width}
                      height={size.height}
                      image={selectedFile}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="flex justify-center w-full ">
                    <Pagination
                      className="w-fit gap-8 border-none bg-transparent"
                      defaultValue={editedFilesData && editedFilesData.findIndex((file) => file.id === selectedFile.id)}
                      pageCount={editedFilesData?.length}
                      prevBtn={
                        <Icon description="Previous" side="top">
                          <LuChevronLeft />
                        </Icon>
                      }
                      nextBtn={
                        <Icon description="Next" side="top">
                          <LuChevronRight />
                        </Icon>
                      }
                      onPageIndexChange={(idx) =>
                        setSelectedFile({
                          ...editedFilesData[idx],
                          data: `data:${editedFilesData[idx].type};base64,${editedFilesData[idx].data}`,
                        })
                      }
                    />
                  </div>
                </>
              ) : null}
              {isPending && (
                <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center bg-[#ffffff7f] z-[99]">
                  <div className="flex items-center gap-2 bg-[#fff] p-2 rounded-[8px]">
                    <LuLoader2 className="animate-spin text-[26px] text-[#224b99]" />
                    <div className="font-medium">Processing...</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Modal>
        <Upload
          ref={upload}
          template={<></>}
          acceptTypes={acceptTypes}
          onChange={handleAddFiles}
          multiple
          onProgress={(e) => console.log(e)}
        />
      </>
    );
  }

  return open ? (
    <>
      <div ref={modal} className="fixed top-0 left-0 w-full h-full z-[20] bg-[#fff] flex flex-col overflow-auto">
        {!isCapturing ? (
          <>
            <header className="flex p-2 w-full justify-between items-center sticky top-0 z-[50] bg-white">
              <div className="font-semibold text-[20px]">Image Editor</div>
              <LuX className="cursor-pointer text-[19px]" onClick={onClose} />
            </header>
            <div className="grow flex items-center justify-center">
              {selectedFile && (
                <ImageEditor
                  ref={image}
                  width={size.width}
                  height={size.height}
                  image={selectedFile}
                  onChange={handleChange}
                />
              )}
              {isPending && (
                <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center bg-[#ffffff7f] z-[99]">
                  <div className="flex items-center gap-2 bg-[#fff] p-2 rounded-[8px]">
                    <LuLoader2 className="animate-spin text-[26px] text-[#224b99]" />
                    <div className="font-medium">Processing...</div>
                  </div>
                </div>
              )}
            </div>
            <Browser
              compact
              imageSrcs={editedFilesData?.map?.((file) => ({ ...file, data: `data:${file.type};base64,${file.data}` }))}
              selected={selectedFile}
              onClick={(file) => suspense(() => setSelectedFile(file))}
            />
            <Drawer open={openDrawer} onOpenChange={setOpenDrawer}>
              <div className="border-t p-5 flex w-full items-center justify-between sticky bottom-0 bg-white z-[50]">
                <Dialog>
                  <DialogTrigger>
                    <Icon compact label={"Delete"} onClick={() => setItem("trash")}>
                      <LuTrash2 />
                    </Icon>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Are you absolutely sure?</DialogTitle>
                        <DialogDescription>
                          This action cannot be undone. This will permanently delete your file.
                        </DialogDescription>
                      </DialogHeader>
                      <DialogFooter>
                        <Button variant="destructive" size="sm" onClick={handleDelete} disabled={isCapturing}>
                          delete
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </DialogTrigger>
                </Dialog>
                <Icon compact label="Reset" onClick={() => suspense(handleReset)}>
                  <LuUndo2 />
                </Icon>
                <DrawerTrigger>
                  <Icon compact label="Add">
                    <LuPlusCircle />
                  </Icon>
                </DrawerTrigger>
                <Icon compact label="Rotate" onClick={() => suspense(handleRotate)}>
                  <LuRotateCw />
                </Icon>
                <Icon compact label="Crop" onClick={() => suspense(image.current.crop)}>
                  <LuCrop />
                </Icon>
              </div>
              <DrawerContent>
                <Command className="z-[99]">
                  <CommandList>
                    <CommandGroup>
                      {[
                        {
                          label: "Add File",
                          value: "add-image",
                          icon: <LuPaperclip />,
                          handler: () => upload.current.mount(),
                        },
                        {
                          label: "Capture",
                          value: "capture-image",
                          icon: <LuCamera />,
                          handler: () => setIsCapturing(true),
                        },
                        // {
                        //   label: "Zoom In",
                        //   value: "zoom-in",
                        //   icon: <LuZoomIn />,
                        //   handler: () => {},
                        // },
                        // {
                        //   label: "Zoom Out",
                        //   value: "zoom-out",
                        //   icon: <LuZoomOut />,
                        //   handler: () => {},
                        // },
                      ].map((item) => (
                        <CommandItem
                          key={item.value}
                          onSelect={() => {
                            setOpenDrawer(false);
                            item.handler();
                          }}
                        >
                          <div className="flex items-center p-3 gap-8 text-[14px] w-full">
                            {item.icon} {item.label}
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </DrawerContent>
            </Drawer>
          </>
        ) : (
          <Scanner
            compact
            container={modal.current}
            images={editedFilesData}
            onCapture={handleAddFiles}
            onClose={() => setIsCapturing(false)}
          />
        )}
      </div>
      <Upload
        ref={upload}
        template={<></>}
        acceptTypes={acceptTypes}
        onChange={handleAddFiles}
        multiple
        onProgress={(e) => console.log(e)}
      />
    </>
  ) : null;
});

export default ImageEditorModal;

ImageEditorModal.propTypes = {
  filesData: PropTypes.array,
  editedFilesData: PropTypes.array,
  setFilesData: PropTypes.func,
  onChange: PropTypes.func,
  acceptTypes: PropTypes.array,
  compact: PropTypes.bool,
  onClose: PropTypes.func,
};

function Icon(props) {
  const { description, label, children, active, onClick, className, disabled, side = "bottom", compact } = props;
  if (!compact)
    return (
      <Tooltip side={side} label={description || label}>
        <div
          style={{
            background: active ? "#e9e9e9" : "",
            opacity: disabled ? 0.2 : 1,
            pointerEvents: disabled ? "none" : "auto",
          }}
          className={cn(
            "select-none flex flex-col items-center gap-1 text-[16px] cursor-pointer hover:bg-[#f8f8f8] p-2 w-[80px] rounded-[8px] ",
            className
          )}
          onClick={onClick}
        >
          {children}
          <div className="text-[11px]">{label}</div>
        </div>
      </Tooltip>
    );
  return (
    <div
      className={cn("select-none flex flex-col items-center gap-1 w-20 p-2 rounded-[8px] active:bg-[#f8f8f8]", className)}
      style={{
        background: active ? "#e9e9e9" : "",
        opacity: disabled ? 0.2 : 1,
        pointerEvents: disabled ? "none" : "auto",
      }}
      onClick={onClick}
    >
      {children}
      <div className="text-[11px]">{label}</div>
    </div>
  );
}

Icon.propTypes = {
  description: PropTypes.node,
  label: PropTypes.node,
  children: PropTypes.node,
  active: PropTypes.bool,
  onClick: PropTypes.func,
  className: PropTypes.string,
  side: PropTypes.string,
  disabled: PropTypes.bool,
  compact: PropTypes.bool,
};
